import { DevsuWhiteIcon } from '@/assets';
import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  ConfirmationBannerSliceDefault,
  ConfirmationBannerSliceWithContainer,
} from '../../../prismicio-types';
import { PrismicNextImage } from '@prismicio/next';

enum SLICE_VARIATION {
  default = 'default',
  withContainer = 'withContainer',
}

/**
 * Props for `ConfirmationBanner`.
 */
export type ConfirmationBannerProps =
  SliceComponentProps<Content.ConfirmationBannerSlice>;

/**
 * Component for "ConfirmationBanner" Slices.
 */
const ConfirmationBanner = ({
  slice,
}: ConfirmationBannerProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-6"
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withContainer &&
        withContainerVariation(slice)}
    </section>
  );
};

const defaultVariation = (
  slice: ConfirmationBannerSliceDefault,
): JSX.Element => {
  const { title, content } = slice.primary;
  return (
    <div className="h-[calc(100vh-160px)] flex flex-col justify-center items-center text-white text-center gap-y-11">
      <div className="text-3xl md:text-5xl font-extrabold max-w-3xl break-words">
        <PrismicRichText field={title} />
      </div>
      <DevsuWhiteIcon />
      <div className="text-xl font-medium">
        <PrismicRichText field={content} />
      </div>
    </div>
  );
};

const withContainerVariation = (
  slice: ConfirmationBannerSliceWithContainer,
): JSX.Element => {
  const { header_image: headerImage, icon, title, content } = slice.primary;
  return (
    <div className="p-10 md:p-16 rounded-[20px] backdrop-blur-[47.5px] shadow-lg bg-white my-12 flex flex-col gap-y-10 items-center text-center">
      <PrismicNextImage
        field={headerImage}
        className="w-full max-w-[23.75rem]"
      />
      <PrismicNextImage field={icon} />
      <div className="max-w-[45rem]">
        <PrismicRichText field={title} />
      </div>
      <div className="max-w-[51rem] text-medium-normal">
        <PrismicRichText field={content} />
      </div>
    </div>
  );
};

export default ConfirmationBanner;
